import React, { useCallback, useEffect, useState } from "react";
import { FiChevronDown, FiChevronRight, FiDatabase, FiDownload, FiPlay, FiTrash2 } from "react-icons/fi";
import { usePermissions } from "../../Auth/PermissionsContext";
import GatedButton from "../../Components/GatedButton";
import { client } from "../../Clients";
import type {
	BackupArchiveDto,
	BackupDestinationDto,
	BackupInstanceDto,
	BackupJobDto,
	BackupMetricsDto,
	RestoreAcceptedDto,
} from "./backupClient";
import ReplaceRestoreModal from "./ReplaceRestoreModal";
import RestoreProgressModal from "./RestoreProgressModal";
import UploadDropzone from "./UploadDropzone";
import { DataTable, Pill, SectionCard, StatCard, extractApiError, formatAgeSeconds, formatBytes, formatDateTime } from "./ui";

/**
 * Section « Base de données » de la page Sauvegardes. Sonde l'API toutes les 2 s et affiche :
 * indicateurs (globaux + par destination), santé des destinations, archives (télécharger /
 * restaurer merge / restaurer remplacement / supprimer), zone d'import d'archives .cbak(.enc),
 * et : repliées sous « Journal des tâches » : les tables instances + tâches récentes.
 *
 * Seules les sauvegardes complètes se déclenchent ici : le mode incrémental n'existe plus
 * côté API (400). La restauration en remplacement passe par la modale à confirmation tapée ;
 * les DEUX modes répondent 202 { jobId } et basculent sur la vue de suivi
 * (RestoreProgressModal), qui sonde le job toutes les 2 s jusqu'au toast final.
 *
 * Gating : la consultation exige feature:backup:read (gating de route) ; toute action : * y compris le téléchargement d'archive, qui exfiltre la base entière (convention serveur
 * Download* = écriture) : exige feature:backup:write (GatedButton / zone d'import désactivée).
 */
const WRITE_TOKEN = "feature:backup:write";

const DatabaseBackupSection: React.FC<{ pollIntervalMs?: number; restorePollIntervalMs?: number }> = ({
	pollIntervalMs = 2000,
	restorePollIntervalMs = 2000,
}) => {
	const { canFeatureWrite } = usePermissions();
	const canWrite = canFeatureWrite("backup");
	const [jobs, setJobs] = useState<BackupJobDto[]>([]);
	const [destinations, setDestinations] = useState<BackupDestinationDto[]>([]);
	const [instances, setInstances] = useState<BackupInstanceDto[]>([]);
	const [metrics, setMetrics] = useState<BackupMetricsDto | null>(null);
	const [archives, setArchives] = useState<BackupArchiveDto[]>([]);
	const [creating, setCreating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
	const [busyArchiveId, setBusyArchiveId] = useState<string | null>(null);
	const [actionMessage, setActionMessage] = useState<string | null>(null);
	const [replaceTarget, setReplaceTarget] = useState<BackupArchiveDto | null>(null);
	// Restauration acceptée (202) en cours de suivi : les deux modes convergent ici.
	const [activeRestore, setActiveRestore] = useState<RestoreAcceptedDto | null>(null);
	const [journalOpen, setJournalOpen] = useState(false);
	// Import d'archive : progression, erreurs détaillées (400) et proposition d'écrasement (409).
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const [uploadError, setUploadError] = useState<{ message: string; details: string[] } | null>(null);
	const [conflictFile, setConflictFile] = useState<File | null>(null);

	const refresh = useCallback(async () => {
		try {
			const [j, d, i, m, a] = await Promise.all([
				client.backup.listJobs({ take: 50 }),
				client.backup.listDestinations(),
				client.backup.listInstances(),
				client.backup.getMetrics(),
				client.backup.listArchives(),
			]);
			setJobs(j);
			setDestinations(d);
			setInstances(i);
			setMetrics(m);
			setArchives(a);
			setError(null);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}, []);

	useEffect(() => {
		refresh();
		const id = setInterval(refresh, pollIntervalMs);
		return () => clearInterval(id);
	}, [refresh, pollIntervalMs]);

	const createFullBackup = useCallback(async () => {
		setCreating(true);
		try {
			await client.backup.createJob({ kind: "full", scope: "both" });
			await refresh();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setCreating(false);
		}
	}, [refresh]);

	// Suppression définitive d'une archive : deux temps dans l'interface (Supprimer → Confirmer),
	// et l'API exige en plus l'en-tête destructif, envoyé par le client.
	const deleteArchive = useCallback(
		async (archive: BackupArchiveDto) => {
			setBusyArchiveId(archive.id);
			setActionMessage(null);
			try {
				const result = await client.backup.deleteArchive(archive.id, archive.destinationId || undefined);
				setActionMessage(`Archive ${result.id} supprimée (${result.filesDeleted} fichiers).`);
				await refresh();
			} catch (e) {
				setError(e instanceof Error ? e.message : String(e));
			} finally {
				setBusyArchiveId(null);
				setConfirmDeleteId(null);
			}
		},
		[refresh]
	);

	// Restauration en mode merge : ré-applique les lignes de l'archive sur la base courante
	// (aucune suppression). Le mode remplacement passe par la modale dédiée. Dans les deux
	// cas l'API répond 202 { jobId } : on bascule sur la vue de suivi, qui sonde le job.
	const restoreArchive = useCallback(async (archive: BackupArchiveDto) => {
		setBusyArchiveId(archive.id);
		setActionMessage(null);
		try {
			const accepted = await client.backup.restore({
				backupId: archive.id,
				destinationId: archive.destinationId,
				mode: "merge",
			});
			setActiveRestore(accepted);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusyArchiveId(null);
		}
	}, []);

	const applyRetention = useCallback(async () => {
		setActionMessage(null);
		try {
			const result = await client.backup.applyRetention();
			setActionMessage(
				result.deleted.length === 0
					? "Rétention appliquée : aucune archive à expirer."
					: `Rétention appliquée : ${result.deleted.length} archive(s) expirée(s), ${result.kept.length} conservée(s).`
			);
			await refresh();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}, [refresh]);

	const downloadArchive = useCallback(async (archive: BackupArchiveDto) => {
		setActionMessage(null);
		try {
			await client.backup.downloadArchive(archive.id, archive.destinationId || undefined);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}, []);

	// Import d'une archive téléchargée : 400 → erreurs de validation détaillées ;
	// 409 (archive déjà présente) → proposer explicitement l'import en écrasant.
	const uploadArchive = useCallback(
		async (file: File, overwrite: boolean) => {
			setUploadError(null);
			setConflictFile(null);
			setActionMessage(null);
			setUploadProgress(0);
			try {
				const imported = await client.backup.uploadArchive(file, {
					overwrite,
					onUploadProgress: (event) => {
						if (event.total) setUploadProgress(Math.round((event.loaded / event.total) * 100));
					},
				});
				setActionMessage(`Archive ${imported.id} importée (épinglée : exclue de la rétention).`);
				await refresh();
			} catch (e) {
				const response = (e as { response?: { status?: number; data?: { error?: string; details?: string[] } } })?.response;
				if (response?.status === 409) {
					setConflictFile(file);
					setUploadError({
						message: response.data?.error ?? "Cette archive existe déjà sur la destination.",
						details: [],
					});
				} else {
					setUploadError({
						message: response?.data?.error ?? extractApiError(e),
						details: response?.data?.details ?? [],
					});
				}
			} finally {
				setUploadProgress(null);
			}
		},
		[refresh]
	);

	const lastFullLabel = formatAgeSeconds(metrics?.lastSuccessfulFullAgeSeconds);

	const isImported = (a: BackupArchiveDto) => a.pinned || (a.generator ?? "").startsWith("import/");

	return (
		<SectionCard
			icon={<FiDatabase className="h-5 w-5" aria-hidden />}
			title="Base de données"
			description="Sauvegardes SQL de l'admin : archives chiffrées, restauration et rétention."
		>
			{error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

			{/* Actions globales */}
			<div className="flex flex-wrap items-center gap-2">
				<GatedButton
					token={WRITE_TOKEN}
					disabled={creating}
					onClick={createFullBackup}
					className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiPlay className="h-4 w-4" aria-hidden />
					{creating ? "Lancement…" : "Lancer une sauvegarde complète"}
				</GatedButton>
				<GatedButton
					token={WRITE_TOKEN}
					onClick={applyRetention}
					title="Expire les archives hors de la politique de rétention (GFS)"
					className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
				>
					<FiTrash2 className="h-4 w-4" aria-hidden />
					Appliquer la rétention
				</GatedButton>
			</div>

			{/* Indicateurs */}
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<StatCard title="Dernière sauvegarde complète" value={lastFullLabel} />
				<StatCard title="Tâches récentes" value={String(metrics?.recentJobs ?? 0)} />
				<StatCard
					title="Tâches dégradées"
					value={String(metrics?.degradedJobs ?? 0)}
					tone={metrics && metrics.degradedJobs > 0 ? "warn" : undefined}
				/>
				<StatCard
					title="Tâches en échec"
					value={String(metrics?.failedJobs ?? 0)}
					tone={metrics && metrics.failedJobs > 0 ? "error" : undefined}
				/>
			</div>

			{/* Empreinte de stockage par destination (une destination en panne n'affiche que son erreur) */}
			{(metrics?.destinations?.length ?? 0) > 0 && (
				<div>
					<h3 className="text-sm font-semibold text-slate-700">Stockage par destination</h3>
					<div className="mt-2 grid gap-3 lg:grid-cols-2">
						{metrics!.destinations.map((d) => (
							<div
								key={d.destinationId}
								className={[
									"rounded-xl border p-3",
									d.error ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50",
								].join(" ")}
							>
								<div className="flex items-center justify-between gap-2">
									<span className="text-sm font-semibold text-slate-800">
										<code className="text-[12px]">{d.destinationId}</code>
									</span>
									{d.error ? <Pill label="injoignable" tone="red" /> : <Pill label={`${d.archiveCount} archive(s)`} tone="slate" />}
								</div>
								{d.error ? (
									<p className="mt-2 text-sm font-medium text-red-700">{d.error}</p>
								) : (
									<dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] text-slate-700 sm:grid-cols-4">
										<div>
											<dt className="text-[11px] uppercase tracking-wide text-slate-500">Archives</dt>
											<dd className="font-semibold">{formatBytes(d.archiveBytes)}</dd>
										</div>
										<div>
											<dt className="text-[11px] uppercase tracking-wide text-slate-500">Médias</dt>
											<dd className="font-semibold">{formatBytes(d.mediaBytes)}</dd>
										</div>
										<div>
											<dt className="text-[11px] uppercase tracking-wide text-slate-500">Total</dt>
											<dd className="font-semibold">{formatBytes(d.totalBytes)}</dd>
										</div>
										<div>
											<dt className="text-[11px] uppercase tracking-wide text-slate-500">Dernière complète</dt>
											<dd className="font-semibold">{formatAgeSeconds(d.lastArchiveAgeSeconds)}</dd>
										</div>
									</dl>
								)}
							</div>
						))}
					</div>
				</div>
			)}

			{/* Destinations */}
			<div>
				<h3 className="text-sm font-semibold text-slate-700">Destinations ({destinations.length})</h3>
				<DataTable
					headers={["Identifiant", "Type", "Chiffrement", "Indicateurs", "Santé"]}
					rows={destinations.map((d) => ({
						key: d.id,
						cells: [
							d.id,
							d.kind,
							d.encryption,
							[d.primary ? "principale" : null, d.enabled ? null : "désactivée"].filter(Boolean).join(", ") || "-",
							<Pill
								label={d.health === "healthy" ? "saine" : d.health === "degraded" ? "dégradée" : d.health === "unhealthy" ? "défaillante" : d.health}
								tone={d.health === "healthy" ? "green" : d.health === "degraded" ? "amber" : d.health === "unhealthy" ? "red" : "slate"}
							/>,
						],
					}))}
					emptyLabel="Aucune destination configurée."
				/>
			</div>

			{/* Archives */}
			<div>
				<h3 className="text-sm font-semibold text-slate-700">Archives ({archives.length})</h3>
				{actionMessage && (
					<div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{actionMessage}</div>
				)}
				<DataTable
					headers={["Identifiant", "Type", "Date", "Lignes", "Taille", "Chiffrement", "Actions"]}
					rows={archives.map((a) => ({
						key: a.id,
						cells: [
							<span className="whitespace-nowrap">
								<code className="text-[11px]">{a.id}</code>
								{isImported(a) && (
									<span className="ml-2">
										<Pill label="importée" tone="sky" />
									</span>
								)}
							</span>,
							a.kind,
							formatDateTime(a.startedUtc),
							String(a.rowCount),
							formatBytes(a.bytesWritten),
							a.encryption,
							<span className="flex flex-wrap items-center gap-1.5 whitespace-nowrap">
								{/* Convention serveur : le téléchargement d'archive exfiltre la base entière → écriture. */}
								<GatedButton
									token={WRITE_TOKEN}
									onClick={() => downloadArchive(a)}
									title="Télécharge le conteneur d'archive (chiffré le cas échéant)"
									className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
								>
									<FiDownload className="h-3.5 w-3.5" aria-hidden />
									Télécharger
								</GatedButton>
								<GatedButton
									token={WRITE_TOKEN}
									disabled={busyArchiveId !== null}
									onClick={() => restoreArchive(a)}
									title="Restaure les lignes de cette archive dans la base (mode fusion : aucune suppression)"
									className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
								>
									{busyArchiveId === a.id ? "…" : "Restaurer"}
								</GatedButton>
								<GatedButton
									token={WRITE_TOKEN}
									disabled={busyArchiveId !== null}
									onClick={() => setReplaceTarget(a)}
									title="Remplace TOUTES les données de la base par cette archive (confirmation exigée)"
									className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
								>
									Restaurer (remplacement)…
								</GatedButton>
								{confirmDeleteId === a.id ? (
									<>
										<GatedButton
											token={WRITE_TOKEN}
											disabled={busyArchiveId !== null}
											onClick={() => deleteArchive(a)}
											className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
										>
											Confirmer la suppression
										</GatedButton>
										<button
											type="button"
											disabled={busyArchiveId !== null}
											onClick={() => setConfirmDeleteId(null)}
											className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
										>
											Annuler
										</button>
									</>
								) : (
									<GatedButton
										token={WRITE_TOKEN}
										disabled={busyArchiveId !== null}
										onClick={() => setConfirmDeleteId(a.id)}
										title="Supprime définitivement cette archive de la destination"
										className="rounded-lg px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
									>
										Supprimer
									</GatedButton>
								)}
							</span>,
						],
					}))}
					emptyLabel="Aucune archive sur la destination : lancez une sauvegarde ci-dessus."
				/>
			</div>

			{/* Import d'archive */}
			<div>
				<h3 className="text-sm font-semibold text-slate-700">Importer une archive</h3>
				<p className="mb-2 text-xs text-slate-500">
					Réimporte une archive téléchargée depuis cette page (ou un autre environnement). Les archives importées
					sont épinglées : la rétention ne les supprime jamais.
				</p>
				<UploadDropzone
					accept=".cbak,.cbak.enc"
					label="Déposez une archive ici, ou cliquez pour choisir un fichier"
					hint="Formats acceptés : .cbak (clair) et .cbak.enc (chiffré)"
					inputLabel="Importer une archive de base de données (.cbak, .cbak.enc)"
					progress={uploadProgress}
					disabled={!canWrite}
					onFile={(file) => uploadArchive(file, false)}
				/>
				{!canWrite && (
					<p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
						L'import d'archive nécessite la permission d'écriture sur les sauvegardes.
					</p>
				)}
				{uploadError && (
					<div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
						<p className="font-medium">{uploadError.message}</p>
						{uploadError.details.length > 0 && (
							<ul className="mt-1 list-inside list-disc text-xs">
								{uploadError.details.map((d, i) => (
									<li key={i}>{d}</li>
								))}
							</ul>
						)}
						{conflictFile && (
							<GatedButton
								token={WRITE_TOKEN}
								onClick={() => uploadArchive(conflictFile, true)}
								className="mt-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
							>
								Importer en écrasant
							</GatedButton>
						)}
					</div>
				)}
			</div>

			{/* Journal des tâches (replié par défaut) */}
			<div className="border-t border-slate-100 pt-3">
				<button
					type="button"
					onClick={() => setJournalOpen((v) => !v)}
					aria-expanded={journalOpen}
					className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900"
				>
					{journalOpen ? <FiChevronDown className="h-4 w-4" aria-hidden /> : <FiChevronRight className="h-4 w-4" aria-hidden />}
					Journal des tâches
				</button>
				{journalOpen && (
					<div className="mt-3 space-y-5">
						<div>
							<h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Instances ({instances.length})</h4>
							<DataTable
								headers={["Instance", "Tâches en cours", "Verrou GC", "Dernier signal"]}
								rows={instances.map((inst) => ({
									key: inst.instanceId,
									cells: [
										<span>
											<code className="text-[11px]">{inst.instanceId}</code>
											{inst.isCurrent && <span className="ml-2 text-[11px] font-semibold text-emerald-700">(cette instance)</span>}
										</span>,
										String(inst.runningJobs),
										inst.holdsGcLock ? "détenu" : "-",
										formatDateTime(inst.lastSeenUtc),
									],
								}))}
								emptyLabel="Aucune instance ne s'est signalée au cours des 30 dernières minutes."
							/>
						</div>
						<div>
							<h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tâches récentes ({jobs.length})</h4>
							<DataTable
								headers={["Identifiant", "Type", "Statut", "Destination", "Instance", "Lignes", "Taille", "Démarrée"]}
								rows={jobs.map((j) => ({
									key: j.id,
									cells: [
										<code className="text-[11px]">{j.id}</code>,
										j.kind,
										<Pill
											label={
												j.status === "Completed"
													? j.isDegraded
														? "Terminée (dégradée)"
														: "Terminée"
													: j.status === "Failed"
													? "Échec"
													: j.status === "Running"
													? "En cours"
													: j.status
											}
											tone={
												j.status === "Completed" ? (j.isDegraded ? "amber" : "green") : j.status === "Failed" ? "red" : j.status === "Running" ? "sky" : "slate"
											}
										/>,
										j.destinationId,
										j.instanceId ?? "-",
										String(j.rowsExported),
										formatBytes(j.bytesWritten),
										formatDateTime(j.startedAt),
									],
								}))}
								emptyLabel="Aucune tâche de sauvegarde pour le moment."
							/>
						</div>
					</div>
				)}
			</div>

			{replaceTarget && (
				<ReplaceRestoreModal
					archive={replaceTarget}
					onClose={() => setReplaceTarget(null)}
					onAccepted={(accepted) => {
						// 202 : la modale de confirmation laisse place à la vue de suivi du job.
						setReplaceTarget(null);
						setActiveRestore(accepted);
					}}
				/>
			)}

			{activeRestore && (
				<RestoreProgressModal
					accepted={activeRestore}
					pollIntervalMs={restorePollIntervalMs}
					onClose={() => setActiveRestore(null)}
					onFinished={() => void refresh()}
				/>
			)}
		</SectionCard>
	);
};

export default DatabaseBackupSection;
